(() => {
  const DISMISS_PREFIX = 'announcement-dismiss-';

  function hideAnnouncementSection(el) {
    const section = el?.closest?.('.announcement-bar-section');
    if (!section) return;
    section.hidden = true;
    section.style.display = 'none';
    document.documentElement.style.setProperty('--announcement-height', '0px');
  }

  function parseEndMs(raw, fallbackMs) {
    if (raw) {
      let normalized = String(raw).trim().replace(' ', 'T');
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(normalized)) {
        normalized += ':00';
      }
      const parsed = Date.parse(normalized);
      if (!Number.isNaN(parsed)) return parsed;
    }
    const ms = Number(fallbackMs);
    if (Number.isFinite(ms) && ms > 0) return ms;
    return null;
  }

  function pad(value) {
    return String(Math.max(0, value)).padStart(2, '0');
  }

  class AnnouncementCountdown extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.endMs = parseEndMs(this.dataset.end, this.dataset.endMs);
      this.expiredAction = this.dataset.expiredAction || 'show_message';
      this.unitsEl = this.querySelector('[data-countdown-units]');
      this.expiredEl = this.querySelector('[data-countdown-expired]');
      this.daysEl = this.querySelector('[data-days]');
      this.hoursEl = this.querySelector('[data-hours]');
      this.minutesEl = this.querySelector('[data-minutes]');
      this.secondsEl = this.querySelector('[data-seconds]');
      this.tick();
      this._timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this._timer) window.clearInterval(this._timer);
    }

    tick() {
      if (this.endMs == null) return;
      const distance = this.endMs - Date.now();
      if (distance <= 0) {
        this.expire();
        return;
      }
      const days = Math.floor(distance / 86400000);
      const hours = Math.floor((distance % 86400000) / 3600000);
      const minutes = Math.floor((distance % 3600000) / 60000);
      const seconds = Math.floor((distance % 60000) / 1000);
      if (this.daysEl) this.daysEl.textContent = pad(days);
      if (this.hoursEl) this.hoursEl.textContent = pad(hours);
      if (this.minutesEl) this.minutesEl.textContent = pad(minutes);
      if (this.secondsEl) this.secondsEl.textContent = pad(seconds);
    }

    expire() {
      if (this._timer) {
        window.clearInterval(this._timer);
        this._timer = null;
      }
      if (this.expiredAction === 'hide_bar') {
        hideAnnouncementSection(this);
        return;
      }
      this.classList.add('is-expired');
      if (this.unitsEl) this.unitsEl.hidden = true;
      if (this.expiredEl) this.expiredEl.hidden = false;
    }
  }

  class AnnouncementMarquee extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.track = this.querySelector('[data-marquee-track]');
      this.group = this.querySelector('[data-marquee-group]');
      if (!this.track || !this.group) return;

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) return;

      if (this.track.children.length < 2) {
        this.track.appendChild(this.group.cloneNode(true));
      }

      this.syncDirection();
      this.addEventListener('mouseenter', this.pause);
      this.addEventListener('mouseleave', this.resume);
      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.resume);
    }

    disconnectedCallback() {
      this.removeEventListener('mouseenter', this.pause);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('focusin', this.pause);
      this.removeEventListener('focusout', this.resume);
    }

    syncDirection() {
      const setting = this.dataset.direction || 'auto';
      const host = this.closest('.announcement-bar__marquee');
      if (!host) return;
      if (setting === 'auto') {
        const rtl = document.documentElement.getAttribute('dir') === 'rtl';
        host.dataset.direction = rtl ? 'right' : 'left';
      } else {
        host.dataset.direction = setting;
      }
    }

    pause = () => {
      if (this.dataset.pauseHover === 'false') return;
      this.classList.add('is-paused');
    };

    resume = () => {
      this.classList.remove('is-paused');
    };
  }

  class AnnouncementDismiss extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.key = this.dataset.storageKey || `${DISMISS_PREFIX}${this.dataset.sectionId || ''}`;
      this.button = this.querySelector('button');
      if (this.button) {
        this.button.addEventListener('click', this.onDismiss);
      }
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.onDismiss);
    }

    onDismiss = () => {
      try {
        sessionStorage.setItem(this.key, '1');
      } catch (e) {
        /* private mode */
      }
      hideAnnouncementSection(this);
    };
  }

  if (!customElements.get('announcement-countdown')) {
    customElements.define('announcement-countdown', AnnouncementCountdown);
  }
  if (!customElements.get('announcement-marquee')) {
    customElements.define('announcement-marquee', AnnouncementMarquee);
  }
  if (!customElements.get('announcement-dismiss')) {
    customElements.define('announcement-dismiss', AnnouncementDismiss);
  }
})();
